import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Building, Users, TrendingUp, ArrowDown } from 'lucide-react';

export const CommercialLeasing = () => {
  return (
    <div className="service-detail-page">
      {/* Breadcrumb */}
      <div className="breadcrumb-container">
        <Link to="/" className="breadcrumb-link">
          <ArrowLeft size={18} />
          Back to Home
        </Link>
        <span className="breadcrumb-separator">/</span>
        <Link to="/services/real-estate" className="breadcrumb-link">Real Estate Advisory</Link>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Commercial Leasing</span>
      </div>

      {/* Hero Section */}
      <section className="service-detail-hero">
        <div className="service-detail-icon">
          <Building size={48} />
        </div>
        <h1 className="service-detail-title">Commercial Leasing</h1>
        <p className="service-detail-subtitle">
          Connecting commercial real estate developers and asset owners with expanding brands and occupiers through structured leasing and occupancy solutions.
        </p>
      </section>

      {/* What We Do */}
      <section className="service-detail-section">
        <div className="service-detail-content">
          <h2 className="service-section-title">What We Do</h2>
          <p className="service-section-text" style={{maxWidth: '950px', margin: '0 auto 3rem', textAlign: 'center'}}>
            HSNL Advisory provides commercial leasing and occupier advisory services by connecting developers, commercial asset owners and retail destinations with brands seeking strategically located spaces for expansion.
          </p>
          <p className="service-section-text" style={{maxWidth: '950px', margin: '0 auto 3rem', textAlign: 'center'}}>
            We work on both sides of the transaction — supporting developers and landlords in securing suitable occupiers, while helping brands identify commercial locations aligned with their expansion strategy, format and commercial requirements.
          </p>

          {/* Two Service Pillars */}
          <div className="leasing-pillars-grid">
            {/* Developer & Asset Owner Leasing Advisory */}
            <div className="leasing-pillar-card">
              <div className="service-block-icon">
                <Building size={36} />
              </div>
              <h3 className="service-block-title">Developer & Asset Owner Leasing Advisory</h3>
              <p className="service-block-intro">
                We assist developers and commercial property owners in identifying and engaging suitable brands and occupiers for available commercial spaces.
              </p>
              <p className="leasing-subheading">Our role may include:</p>
              <ul className="service-block-list">
                <li>Understanding available inventory and project positioning</li>
                <li>Understanding the commercial format and tenant mix</li>
                <li>Identifying relevant brands and occupiers</li>
                <li>Brand introductions and initial engagement</li>
                <li>Coordinating site visits</li>
                <li>Facilitating commercial discussions</li>
                <li>Supporting lease / occupancy negotiations</li>
                <li>Facilitating discussions through transaction closure</li>
              </ul>
              <p className="leasing-subheading">We cater to commercial spaces across formats such as:</p>
              <ul className="service-block-list">
                <li>Shopping malls</li>
                <li>High-street retail</li>
                <li>Mixed-use developments</li>
                <li>Standalone commercial properties</li>
                <li>Commercial complexes</li>
                <li>Large-format retail spaces</li>
                <li>Lifestyle and destination retail</li>
                <li>Other suitable commercial developments</li>
              </ul>
            </div>

            {/* Brand & Occupier Expansion Advisory */}
            <div className="leasing-pillar-card">
              <div className="service-block-icon">
                <Users size={36} />
              </div>
              <h3 className="service-block-title">Brand & Occupier Expansion Advisory</h3>
              <p className="service-block-intro">
                We work with brands and occupiers seeking commercial spaces for expansion and help identify suitable properties based on location, project profile, catchment, space requirement and commercial structure.
              </p>
              <p className="leasing-subheading">Our role may include:</p>
              <ul className="service-block-list">
                <li>Understanding brand expansion requirements</li>
                <li>Location and micro-market identification</li>
                <li>Identifying suitable commercial properties</li>
                <li>Developer / landlord introductions</li>
                <li>Property and project shortlisting</li>
                <li>Site visit coordination</li>
                <li>Commercial discussions</li>
                <li>Lease / occupancy structuring</li>
                <li>Transaction facilitation</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Commercial Structures We Facilitate */}
      <section className="service-detail-section alt-bg">
        <div className="service-detail-content">
          <h2 className="service-section-title">Commercial Structures We Facilitate</h2>
          
          <div className="commercial-structures-grid">
            <div className="commercial-structure-card">
              <div className="structure-icon">
                <Building size={28} />
              </div>
              <h4 className="structure-title">Rental / Lease</h4>
              <p className="structure-description">
                Conventional commercial leasing structures where the brand or occupier takes space against an agreed rental, tenure and other mutually agreed lease terms.
              </p>
            </div>

            <div className="commercial-structure-card">
              <div className="structure-icon">
                <TrendingUp size={28} />
              </div>
              <h4 className="structure-title">Minimum Guarantee (MG)</h4>
              <p className="structure-description">
                Commercial arrangements where the occupier operates under an agreed Minimum Guarantee structure, subject to mutually agreed commercial terms.
              </p>
            </div>

            <div className="commercial-structure-card">
              <div className="structure-icon">
                <Users size={28} />
              </div>
              <h4 className="structure-title">FOCO</h4>
              <p className="structure-subtitle">Franchise Owned & Company Operated</p>
              <p className="structure-description">
                Facilitating suitable opportunities where the franchise / investment structure and brand-operated model are commercially aligned between the relevant parties.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Developers & Commercial Assets We Work With */}
      <section className="service-detail-section">
        <div className="service-detail-content">
          <h2 className="service-section-title">Developers & Commercial Assets We Work With</h2>
          <p className="service-section-text" style={{textAlign: 'center', marginBottom: '3rem', maxWidth: '800px', margin: '0 auto 3rem'}}>
            We engage with developers, commercial property owners and established real estate platforms across suitable leasing opportunities.
          </p>

          <div className="developer-logos-grid">
            <div className="developer-logo-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/lbws2c4i_Migsun%20Logo.png" 
                alt="Migsun" 
                className="developer-logo"
              />
            </div>
            <div className="developer-logo-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/yvhilk3h_images.jpg" 
                alt="NX One" 
                className="developer-logo"
              />
            </div>
            <div className="developer-logo-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/mz6rmc0g_emaarindia_logo.jpg" 
                alt="Emaar India" 
                className="developer-logo"
              />
            </div>
            <div className="developer-logo-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/936ffrg4_Paras%20Logo.jpg" 
                alt="Paras Buildtech" 
                className="developer-logo"
              />
            </div>
            <div className="developer-logo-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/mjd6iviv_Unity%20Logo.jpg" 
                alt="Unity Group" 
                className="developer-logo"
              />
            </div>
            <div className="developer-logo-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/e2zhx7aa_goodearth_infra_logo.jpg" 
                alt="Good Earth Infra" 
                className="developer-logo"
              />
            </div>
            <div className="developer-logo-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/tv939afj_tapasya-logo.png" 
                alt="Tapasya One" 
                className="developer-logo"
              />
            </div>
            <div className="developer-logo-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/sb7hitv9_Vatika%20logo.png" 
                alt="Vatika" 
                className="developer-logo"
              />
            </div>
            <div className="developer-logo-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/7d87qy6j_JMD-logo.webp" 
                alt="JMD" 
                className="developer-logo"
              />
            </div>
            <div className="developer-logo-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/e6mwy2f6_worldmark%20logo.png" 
                alt="Worldmark" 
                className="developer-logo"
              />
            </div>
            <div className="developer-logo-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/1g8ow13r_elan%20logo.png" 
                alt="Elan" 
                className="developer-logo"
              />
            </div>
            <div className="developer-logo-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/s1rag5oo_RPS-Group-color.png" 
                alt="RPS Group" 
                className="developer-logo"
              />
            </div>
            <div className="developer-logo-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/cgell57a_Satya%20Logo.jpg" 
                alt="Satya Group" 
                className="developer-logo"
              />
            </div>
            <div className="developer-logo-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/ks0nqz7j_Trehan%20logo.jpg" 
                alt="Trehan Group" 
                className="developer-logo"
              />
            </div>
            <div className="developer-logo-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/3xbwuclx_Picasso%20logo.jpg" 
                alt="Picasso Centre" 
                className="developer-logo"
              />
            </div>
            <div className="developer-logo-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/q2a6kkp9_noida_world_one_logo.jpg" 
                alt="Noida World One" 
                className="developer-logo"
              />
              <span className="developer-subtitle">CBS</span>
            </div>
          </div>
        </div>
      </section>

      {/* Brands & Occupiers We Work With */}
      <section className="service-detail-section alt-bg">
        <div className="service-detail-content">
          <h2 className="service-section-title">Brands & Occupiers We Work With</h2>
          <p className="service-section-text" style={{textAlign: 'center', marginBottom: '3rem', maxWidth: '800px', margin: '0 auto 3rem'}}>
            We engage with retail, fashion, lifestyle and consumer brands seeking suitable commercial locations for expansion.
          </p>

          <div className="brand-logos-grid">
            <div className="brand-logo-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/xmrd00to_basil%20logo.jpeg" 
                alt="BASIL" 
                className="brand-logo"
              />
            </div>
            <div className="brand-logo-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/1yfpnyn5_fashion%20factory%20logo.jpg" 
                alt="Reliance Fashion Factory" 
                className="brand-logo"
              />
            </div>
            <div className="brand-logo-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/kjg3s1oc_john%20players%20logo.png" 
                alt="John Players" 
                className="brand-logo"
              />
            </div>
            <div className="brand-logo-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/iancpco9_amante-logo-png_seeklogo-589044.png" 
                alt="Amante" 
                className="brand-logo"
              />
            </div>
            <div className="brand-logo-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/simyoz2x_Clovia-Logo.webp" 
                alt="Clovia" 
                className="brand-logo"
              />
            </div>
            <div className="brand-logo-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/670ia7ak_Performax%20Logo.jpg" 
                alt="Performax" 
                className="brand-logo"
              />
            </div>
            <div className="brand-logo-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/pl7noqo2_Zivame%20logo.png" 
                alt="Zivame" 
                className="brand-logo"
              />
            </div>
            <div className="brand-logo-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/0zi3ofhz_lee%20cooper%20logo.png" 
                alt="Lee Cooper" 
                className="brand-logo"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Connecting Properties with Brands */}
      <section className="service-detail-section">
        <div className="service-detail-content">
          <h2 className="service-section-title">Connecting Properties with Brands</h2>
          
          <div className="connection-flow">
            <div className="flow-item">
              <div className="flow-box">Developer / Asset Owner</div>
            </div>
            <div className="flow-arrow">
              <ArrowDown size={32} />
            </div>
            <div className="flow-item">
              <div className="flow-box highlight">HSNL Advisory</div>
            </div>
            <div className="flow-arrow">
              <ArrowDown size={32} />
            </div>
            <div className="flow-item">
              <div className="flow-box">Brand / Occupier</div>
            </div>
          </div>

          <p className="service-section-text" style={{textAlign: 'center', marginTop: '2.5rem', maxWidth: '800px', margin: '2.5rem auto 0'}}>
            We facilitate the process from requirement identification and introductions through location evaluation, site visits, commercial discussions and transaction coordination.
          </p>
        </div>
      </section>

      {/* Who We Support */}
      <section className="service-detail-section alt-bg">
        <div className="service-detail-content">
          <h2 className="service-section-title">Who We Support</h2>
          
          <div className="who-we-support-grid">
            <div className="support-card">
              <h4 className="support-title">Developers & Landlords</h4>
              <p className="support-description">
                Seeking suitable brands and occupiers for their commercial properties.
              </p>
            </div>

            <div className="support-card">
              <h4 className="support-title">Retail & Lifestyle Brands</h4>
              <p className="support-description">
                Seeking suitable commercial locations for expansion.
              </p>
            </div>

            <div className="support-card">
              <h4 className="support-title">Commercial Asset Owners</h4>
              <p className="support-description">
                Looking to improve occupancy and tenant mix across their properties.
              </p>
            </div>

            <div className="support-card">
              <h4 className="support-title">Expansion Teams & Occupiers</h4>
              <p className="support-description">
                Evaluating commercial spaces based on location, format, catchment and commercial viability.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="service-detail-section">
        <div className="service-detail-content">
          <div className="service-cta-box">
            <h2 className="cta-title">Discuss a Commercial Leasing Requirement</h2>
            <p className="cta-text">
              Whether you are a developer looking for suitable brands for your commercial property or a brand evaluating locations for expansion, connect with HSNL Advisory to discuss your requirement.
            </p>
            <Link to="/#contact" className="btn-primary">
              Discuss a Leasing Opportunity
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
